import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { useState } from 'react';

export const InputComponentsSection = () => {
  const [dropdownValue, setDropdownValue] = useState<string | null>(null);
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const [timeValue, setTimeValue] = useState<Date | null>(null);

  const dropdownOptions = [
    { label: 'Option 1', value: 'option1' },
    { label: 'Option 2', value: 'option2' },
    { label: 'Option 3', value: 'option3' },
  ];

  const cities = [
    { label: 'New York', value: 'NY' },
    { label: 'Los Angeles', value: 'LA' },
    { label: 'Chicago', value: 'CHI' },
    { label: 'Houston', value: 'HOU' },
  ];

  return (
    <div id='input-components' className='space-y-6'>
      <div>
        <h2 className='text-2xl font-bold mb-2'>Input Components</h2>
        <p className='text-gray-600 mb-4'>
          Form input components for user interaction and data entry.
        </p>
      </div>

      {/* InputText */}
      <Card title='InputText' className='mb-4'>
        <div className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label
                htmlFor='input-default'
                className='block text-sm font-medium mb-2'
              >
                Default
              </label>
              <InputText
                id='input-default'
                placeholder='Enter text...'
                className='w-full'
              />
            </div>
            <div>
              <label
                htmlFor='input-disabled'
                className='block text-sm font-medium mb-2'
              >
                Disabled
              </label>
              <InputText
                id='input-disabled'
                placeholder='Disabled input'
                disabled
                className='w-full'
              />
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label
                htmlFor='input-search'
                className='block text-sm font-medium mb-2'
              >
                With Icon (Search)
              </label>
              <IconField iconPosition='left'>
                <InputIcon className='pi pi-search' />
                <InputText
                  id='input-search'
                  placeholder='Search...'
                  className='w-full'
                />
              </IconField>
            </div>
            <div>
              <label
                htmlFor='input-invalid'
                className='block text-sm font-medium mb-2'
              >
                Invalid State
              </label>
              <InputText
                id='input-invalid'
                placeholder='Invalid input'
                className='w-full p-invalid'
              />
              <small className='text-red-500'>This field is required</small>
            </div>
          </div>
        </div>
      </Card>

      {/* InputTextarea */}
      <Card title='InputTextarea' className='mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label
              htmlFor='textarea-default'
              className='block text-sm font-medium mb-2'
            >
              Default
            </label>
            <InputTextarea
              id='textarea-default'
              placeholder='Enter multiple lines...'
              rows={4}
              className='w-full'
            />
          </div>
          <div>
            <label
              htmlFor='textarea-autoresize'
              className='block text-sm font-medium mb-2'
            >
              Auto Resize
            </label>
            <InputTextarea
              id='textarea-autoresize'
              placeholder='This textarea auto resizes...'
              autoResize
              rows={4}
              className='w-full'
            />
          </div>
        </div>
      </Card>

      {/* InputNumber */}
      <Card title='InputNumber' className='mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label
              htmlFor='number-basic'
              className='block text-sm font-medium mb-2'
            >
              Basic
            </label>
            <InputNumber
              id='number-basic'
              placeholder='Enter number...'
              className='w-full'
            />
          </div>
          <div>
            <label
              htmlFor='number-minmax'
              className='block text-sm font-medium mb-2'
            >
              Min/Max (0-100)
            </label>
            <InputNumber
              id='number-minmax'
              placeholder='0-100'
              min={0}
              max={100}
              className='w-full'
            />
          </div>
          <div>
            <label
              htmlFor='number-currency'
              className='block text-sm font-medium mb-2'
            >
              Currency
            </label>
            <InputNumber
              id='number-currency'
              placeholder='$0.00'
              mode='currency'
              currency='USD'
              locale='en-US'
              className='w-full'
            />
          </div>
        </div>
      </Card>

      {/* InputSwitch */}
      <Card title='InputSwitch' className='mb-4'>
        <div className='flex items-center gap-8'>
          <div className='flex items-center gap-3'>
            <label htmlFor='switch-off' className='text-sm font-medium'>
              Off State
            </label>
            <InputSwitch
              inputId='switch-off'
              checked={false}
              onChange={() => {}}
            />
          </div>
          <div className='flex items-center gap-3'>
            <label htmlFor='switch-on' className='text-sm font-medium'>
              On State
            </label>
            <InputSwitch
              inputId='switch-on'
              checked={true}
              onChange={() => {}}
            />
          </div>
          <div className='flex items-center gap-3'>
            <label htmlFor='switch-disabled' className='text-sm font-medium'>
              Disabled
            </label>
            <InputSwitch
              inputId='switch-disabled'
              checked={false}
              onChange={() => {}}
              disabled
            />
          </div>
        </div>
      </Card>

      {/* Checkbox */}
      <Card title='Checkbox' className='mb-4'>
        <div className='flex flex-wrap gap-6'>
          <div className='flex items-center gap-2'>
            <Checkbox inputId='cb1' checked={false} onChange={() => {}} />
            <label htmlFor='cb1' className='text-sm'>
              Unchecked
            </label>
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox inputId='cb2' checked={true} onChange={() => {}} />
            <label htmlFor='cb2' className='text-sm'>
              Checked
            </label>
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox
              inputId='cb3'
              checked={false}
              onChange={() => {}}
              disabled
            />
            <label htmlFor='cb3' className='text-sm'>
              Disabled
            </label>
          </div>
        </div>
      </Card>

      {/* Dropdown */}
      <Card title='Dropdown' className='mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label
              htmlFor='dropdown-basic'
              className='block text-sm font-medium mb-2'
            >
              Basic
            </label>
            <Dropdown
              inputId='dropdown-basic'
              value={dropdownValue}
              onChange={e => setDropdownValue(e.value)}
              options={dropdownOptions}
              placeholder='Select an option'
              className='w-full'
            />
          </div>
          <div>
            <label
              htmlFor='dropdown-filter'
              className='block text-sm font-medium mb-2'
            >
              With Filter
            </label>
            <Dropdown
              inputId='dropdown-filter'
              value={dropdownValue}
              onChange={e => setDropdownValue(e.value)}
              options={cities}
              placeholder='Select a city'
              filter
              className='w-full'
            />
          </div>
        </div>
      </Card>

      {/* MultiSelect */}
      <Card title='MultiSelect' className='mb-4'>
        <div>
          <label
            htmlFor='multiselect-cities'
            className='block text-sm font-medium mb-2'
          >
            Select Multiple Cities
          </label>
          <MultiSelect
            inputId='multiselect-cities'
            value={multiSelectValue}
            onChange={e => setMultiSelectValue(e.value)}
            options={cities}
            placeholder='Select cities'
            maxSelectedLabels={3}
            className='w-full md:w-80'
          />
        </div>
      </Card>

      {/* Calendar */}
      <Card title='Calendar' className='mb-4'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div>
            <label
              htmlFor='calendar-date'
              className='block text-sm font-medium mb-2'
            >
              Date Picker
            </label>
            <Calendar
              inputId='calendar-date'
              value={dateValue}
              onChange={e => setDateValue(e.value ?? null)}
              placeholder='Select a date'
              className='w-full'
            />
          </div>
          <div>
            <label
              htmlFor='calendar-time'
              className='block text-sm font-medium mb-2'
            >
              Time Picker
            </label>
            <Calendar
              inputId='calendar-time'
              value={timeValue}
              onChange={e => setTimeValue(e.value ?? null)}
              timeOnly
              placeholder='Select time'
              className='w-full'
            />
          </div>
          <div>
            <label
              htmlFor='calendar-datetime'
              className='block text-sm font-medium mb-2'
            >
              DateTime
            </label>
            <Calendar
              inputId='calendar-datetime'
              value={dateValue}
              onChange={e => setDateValue(e.value ?? null)}
              showTime
              placeholder='Select date & time'
              className='w-full'
            />
          </div>
        </div>
      </Card>

      {/* FileUpload */}
      <Card title='FileUpload' className='mb-4'>
        <FileUpload
          mode='basic'
          name='demo'
          accept='image/*'
          maxFileSize={1000000}
          chooseLabel='Choose File'
        />
      </Card>
    </div>
  );
};
